import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { FormInput } from '~/components/ui/form-items/input'
import { FormTextarea } from '~/components/ui/form-items/textarea'
import { useMutation } from '~/hooks/use-mutation'
import { useQueryClientHelper } from '~/hooks/use-query-client'
import { useToast } from '~/hooks/use-toast'
import { grantUserPointsSchema, type GrantUserPointsFormData } from '~/schemas/grant-user-points'
import { useAppStore } from '~/stores/app'
import type { AdminUser } from '~/types/admin-user'
import { DialogId } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import { QueryKey } from '~/types/query-key'

type UserGrantPointsDialogProps = {
  targetUser: AdminUser | null
}

type GrantPointsResponse = {
  user: AdminUser
  granted: {
    amount: number
    newBalance: number
    transactionId: string
  }
}

export function UserGrantPointsDialog({ targetUser }: UserGrantPointsDialogProps) {
  const { t } = useTranslation()
  const { showSuccess } = useToast()
  const { invalidateQueries } = useQueryClientHelper()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const closeDialog = useAppStore((state) => state.closeDialog)
  const isOpen = openDialogIds.includes(DialogId.GRANT_USER_POINTS)

  const { control, handleSubmit, reset } = useForm<GrantUserPointsFormData>({
    resolver: zodResolver(grantUserPointsSchema),
    defaultValues: {
      amount: 0,
      reason: '',
    },
  })

  useEffect(() => {
    if (isOpen) {
      reset({ amount: 0, reason: '' })
    }
  }, [isOpen, reset])

  const { mutate: grantPoints, isPending } = useMutation<GrantPointsResponse, GrantUserPointsFormData>({
    mutationKey: MutationKey.GRANT_USER_POINTS,
    method: 'PATCH',
    idParams: targetUser ? { id: targetUser.id } : undefined,
    options: {
      onSuccess: (response) => {
        showSuccess(
          t('users.grantPointsSuccess', {
            amount: response.granted.amount.toLocaleString(),
          }),
        )
        invalidateQueries(QueryKey.ADMIN_USERS)
        invalidateQueries(QueryKey.ADMIN_USER_DETAIL)
        closeDialog(DialogId.GRANT_USER_POINTS)
      },
    },
  })

  function onSubmit(data: GrantUserPointsFormData) {
    grantPoints({
      amount: data.amount,
      reason: data.reason?.trim() || undefined,
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog(DialogId.GRANT_USER_POINTS)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.grantPointsTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {targetUser
              ? t('users.grantPointsDescriptionWithUser', { email: targetUser.email })
              : t('users.grantPointsDescription')}
          </p>
          <FormInput
            control={control}
            name="amount"
            label={t('users.grantPointsAmount')}
            type="number"
            required
            id="grant-points-amount-input"
          />
          <FormTextarea
            control={control}
            name="reason"
            label={t('users.grantPointsReason')}
            placeholder={t('users.grantPointsReasonPlaceholder')}
            rows={3}
          />
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => closeDialog(DialogId.GRANT_USER_POINTS)}
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button id="grant-points-confirm-button" type="submit" loading={isPending}>
              {t('users.grantPointsAction')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
